package no.vareflow.app.report

import no.vareflow.app.domain.AppLanguage
import no.vareflow.app.domain.formatPrice
import no.vareflow.app.domain.tr
import no.vareflow.app.domain.formatQuantity

/**
 * Writes a [LaidOutReport] as an A4 PDF with the standard Helvetica fonts (WinAnsi, so æ ø å work),
 * without any library: same file on the phone and in the browser. Every sheet starts on a new
 * page; column titles repeat at the top of each page; pages are numbered "Side x / n".
 */
object PdfWriter {
    private const val PAGE_W = 595.0
    private const val PAGE_H = 842.0
    private const val LEFT = 40.0
    private const val RIGHT = PAGE_W - 40.0
    private const val TOP = PAGE_H - 46.0
    private const val BOTTOM = 52.0
    private const val TABLE_W = RIGHT - LEFT

    private class Page { val ops = Bytes(); }

    fun write(report: LaidOutReport): ByteArray {
        val pages = mutableListOf<Page>()
        var page = Page().also { pages += it }
        var y = TOP
        var columns: List<ReportColumn> = emptyList()
        var header: ReportRow.Header? = null

        fun newPage(repeatHeader: Boolean) {
            page = Page().also { pages += it }
            y = TOP
            val running = if (report.companyName.isNotBlank()) "${report.companyName} · ${report.title}" else report.title
            text(page, LEFT, y, running, 8.0, bold = false, color = GREY)
            y -= 18
            if (repeatHeader) header?.let { drawHeader(page, y, it.columns); y -= 16 }
        }
        fun ensure(space: Double, repeatHeader: Boolean) { if (y - space < BOTTOM) newPage(repeatHeader) }

        // Title block on the first page: logo on the right, company name over the title.
        var logoBottom = y
        report.logo?.let { logo ->
            var h = 44.0
            var w = h * logo.width / logo.height
            if (w > 170.0) { w = 170.0; h = w * logo.height / logo.width }
            val top = TOP + 14
            page.ops.ascii("q ${f(w)} 0 0 ${f(h)} ${f(RIGHT - w)} ${f(top - h)} cm /Im1 Do Q\n")
            logoBottom = top - h
        }
        if (report.companyName.isNotBlank()) {
            text(page, LEFT, y, report.companyName, 11.0, bold = true, color = GREY)
            y -= 20
        }
        text(page, LEFT, y, report.title, 17.0, bold = true, color = INK)
        y -= 16
        text(page, LEFT, y, report.subtitle, 8.5, bold = false, color = GREY)
        y -= 22
        if (report.logo != null) y = minOf(y, logoBottom - 16)

        report.sheets.forEachIndexed { index, sheet ->
            if (index > 0) newPage(repeatHeader = false)
            header = null
            columns = sheet.columns
            val firstHeading = (sheet.rows.firstOrNull() as? ReportRow.Heading)?.text
            if (report.sheets.size > 1 && firstHeading != sheet.name && !(firstHeading ?: "").startsWith(sheet.name)) {
                text(page, LEFT, y, sheet.name, 9.0, bold = true, color = GREY)
                y -= 16
            }
            sheet.rows.forEach { row ->
                when (row) {
                    is ReportRow.Heading -> {
                        ensure(62.0, repeatHeader = false)
                        header = null
                        text(page, LEFT, y - 2, row.text, 12.5, bold = true, color = BLUE)
                        y -= 21
                    }
                    is ReportRow.SubHeading -> {
                        ensure(40.0, repeatHeader = true)
                        text(page, LEFT + 2, y, row.text, 9.5, bold = true, color = MUTED)
                        y -= 14
                    }
                    is ReportRow.Header -> {
                        ensure(32.0, repeatHeader = false)
                        header = row
                        columns = row.columns
                        drawHeader(page, y, row.columns)
                        y -= 16
                    }
                    is ReportRow.Item -> {
                        ensure(13.0, repeatHeader = true)
                        drawCells(page, y, columns, row.cells, bold = false, color = INK)
                        line(page, LEFT, y - 4, RIGHT, y - 4, LINE_LIGHT)
                        y -= 13
                    }
                    is ReportRow.Sum -> {
                        ensure(16.0, repeatHeader = true)
                        line(page, LEFT, y + 9, RIGHT, y + 9, if (row.strong) LINE_DARK else LINE_MID)
                        drawCells(page, y, columns, row.cells, bold = true, color = if (row.strong) INK else MUTED)
                        y -= if (row.strong) 18 else 15
                    }
                    ReportRow.Blank -> y -= 8
                }
            }
        }
        return assemble(report.title, pages, report.language, report.logo)
    }

    // ---------------------------------------------------------------- drawing

    private val INK = Triple(0.12, 0.16, 0.21)
    private val GREY = Triple(0.42, 0.47, 0.51)
    private val MUTED = Triple(0.37, 0.42, 0.47)
    private val BLUE = Triple(0.184, 0.431, 0.620)
    private val LINE_LIGHT = Triple(0.90, 0.91, 0.93)
    private val LINE_MID = Triple(0.75, 0.78, 0.81)
    private val LINE_DARK = Triple(0.45, 0.50, 0.55)
    private val HEADER_FILL = Triple(0.933, 0.945, 0.957)

    private fun columnX(columns: List<ReportColumn>): List<Pair<Double, Double>> {
        val total = columns.sumOf { it.width }.coerceAtLeast(1)
        var x = LEFT
        return columns.map { c -> val w = TABLE_W * c.width / total; (x to w).also { x += w } }
    }

    private fun drawHeader(page: Page, y: Double, columns: List<ReportColumn>) {
        page.ops.ascii("${rgb(HEADER_FILL)} rg ${f(LEFT)} ${f(y - 4.5)} ${f(TABLE_W)} 15 re f\n")
        drawCells(page, y, columns, columns.map { it.title }, bold = true, color = INK, header = true)
    }

    private fun drawCells(page: Page, y: Double, columns: List<ReportColumn>, cells: List<Any?>, bold: Boolean, color: Triple<Double, Double, Double>, header: Boolean = false) {
        val xs = columnX(columns)
        val size = 8.5
        cells.forEachIndexed { i, value ->
            if (value == null || i >= xs.size) return@forEachIndexed
            val column = columns[i]
            val (x, w) = xs[i]
            val numeric = column.kind != CellKind.TEXT
            val raw = when {
                header -> value.toString()
                value is Number -> when (column.kind) {
                    CellKind.MONEY -> formatPrice(value.toDouble()) + " kr"
                    CellKind.COUNT -> value.toLong().toString()
                    else -> formatQuantity(value.toDouble())
                }
                else -> value.toString()
            }
            // The first text cell of a sum row may run into the next empty columns.
            val room = if (!numeric && !header && i == 0 && cells.drop(1).takeWhile { it == null }.isNotEmpty()) {
                val free = cells.drop(1).takeWhile { it == null }.size
                xs.drop(i).take(free + 1).sumOf { it.second } - 6
            } else w - 6
            val shown = fit(raw, bold, size, room)
            val tx = if (numeric) x + w - 3 - width(shown, bold, size) else x + 3
            text(page, tx, y, shown, size, bold, color)
        }
    }

    private fun text(page: Page, x: Double, y: Double, s: String, size: Double, bold: Boolean, color: Triple<Double, Double, Double>) {
        page.ops.ascii("BT ${rgb(color)} rg /${if (bold) "F2" else "F1"} ${f(size)} Tf ${f(x)} ${f(y)} Td (")
        page.ops.bytes(pdfText(s))
        page.ops.ascii(") Tj ET\n")
    }

    private fun line(page: Page, x1: Double, y1: Double, x2: Double, y2: Double, color: Triple<Double, Double, Double>) {
        page.ops.ascii("${rgb(color)} RG 0.6 w ${f(x1)} ${f(y1)} m ${f(x2)} ${f(y2)} l S\n")
    }

    private fun rgb(c: Triple<Double, Double, Double>) = "${f(c.first)} ${f(c.second)} ${f(c.third)}"

    private fun f(v: Double): String {
        val r = kotlin.math.round(v * 100) / 100
        val s = r.toString()
        return if (s.endsWith(".0")) s.dropLast(2) else s
    }

    /** Shortens [s] with "…" until it fits [room] points. */
    private fun fit(s: String, bold: Boolean, size: Double, room: Double): String {
        if (width(s, bold, size) <= room) return s
        var cut = s
        while (cut.isNotEmpty() && width("$cut…", bold, size) > room) cut = cut.dropLast(1)
        return "${cut.trimEnd()}…"
    }

    // ---------------------------------------------------------------- fonts and text encoding

    /** Helvetica / Helvetica-Bold advance widths (1/1000 em) for ASCII 32–126. */
    private val HELV = intArrayOf(
        278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
        556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
        1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
        667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
        333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
        556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584
    )
    private val HELV_BOLD = intArrayOf(
        278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
        556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
        975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
        667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
        333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
        611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584
    )

    private fun charWidth(c: Char, bold: Boolean): Int = when {
        c.code in 32..126 -> (if (bold) HELV_BOLD else HELV)[c.code - 32]
        c == 'æ' -> 889
        c == 'Æ' -> 1000
        c == 'ø' -> 611
        c == 'Ø' -> 778
        c == 'å' -> if (bold) 556 else 556
        c == 'Å' -> if (bold) 722 else 667
        c == '·' -> 278
        c == '…' -> 1000
        c == '–' -> 556
        c == ' ' -> 278
        else -> 556
    }

    internal fun width(s: String, bold: Boolean, size: Double): Double = s.sumOf { charWidth(it, bold) } * size / 1000.0

    /** WinAnsi bytes with PDF string escapes. */
    private fun pdfText(s: String): ByteArray {
        val out = Bytes(s.length + 8)
        for (c in s) {
            val code = when (c) {
                '€' -> 0x80; '…' -> 0x85; '–' -> 0x96; '—' -> 0x97; '•' -> 0x95; '‘' -> 0x91; '’' -> 0x92; '“' -> 0x93; '”' -> 0x94
                ' ' -> 0x20
                else -> if (c.code in 32..255 && c.code !in 0x7F..0x9F) c.code else '?'.code
            }
            if (code == '('.code || code == ')'.code || code == '\\'.code) out.byte('\\'.code)
            out.byte(code)
        }
        return out.toByteArray()
    }

    // ---------------------------------------------------------------- file structure

    private fun assemble(title: String, pages: List<Page>, language: AppLanguage, logo: JpegInfo?): ByteArray {
        val out = Bytes(64 * 1024)
        val offsets = mutableListOf<Int>()
        fun obj(body: (Bytes) -> Unit) {
            offsets += out.size
            out.ascii("${offsets.size} 0 obj\n")
            body(out)
            out.ascii("\nendobj\n")
        }
        out.ascii("%PDF-1.4\n")
        out.byte(0x25); out.byte(0xE2); out.byte(0xE3); out.byte(0xCF); out.byte(0xD3); out.byte(0x0A)
        val pageObjIds = pages.indices.map { 6 + it * 2 }
        // The logo image is written after the pages.
        val imageId = 6 + pages.size * 2
        val xObjects = if (logo != null) " /XObject << /Im1 $imageId 0 R >>" else ""
        obj { it.ascii("<< /Type /Catalog /Pages 2 0 R >>") } // 1
        obj { it.ascii("<< /Type /Pages /Count ${pages.size} /Kids [${pageObjIds.joinToString(" ") { id -> "$id 0 R" }}] >>") } // 2
        obj { it.ascii("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>") } // 3
        obj { it.ascii("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>") } // 4
        obj { it.ascii("<< /Title ("); it.bytes(pdfText(title)); it.ascii(") /Producer (Vareflow) /Creator (Vareflow) >>") } // 5
        pages.forEachIndexed { i, p ->
            // Footer with the page number, now that the page count is known.
            val pageText = tr(language, "Side", "Page") + " ${i + 1} / ${pages.size}"
            text(p, RIGHT - width(pageText, false, 7.5), 30.0, pageText, 7.5, false, GREY)
            text(p, LEFT, 30.0, "Vareflow", 7.5, false, GREY)
            val content = p.ops.toByteArray()
            obj {
                it.ascii("<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${f(PAGE_W)} ${f(PAGE_H)}] ")
                it.ascii("/Resources << /Font << /F1 3 0 R /F2 4 0 R >>$xObjects >> /Contents ${pageObjIds[i] + 1} 0 R >>")
            }
            obj {
                it.ascii("<< /Length ${content.size} >>\nstream\n")
                it.bytes(content)
                it.ascii("\nendstream")
            }
        }
        if (logo != null) obj {
            val space = if (logo.components == 1) "/DeviceGray" else "/DeviceRGB"
            it.ascii("<< /Type /XObject /Subtype /Image /Width ${logo.width} /Height ${logo.height} /ColorSpace $space /BitsPerComponent 8 /Filter /DCTDecode /Length ${logo.bytes.size} >>\nstream\n")
            it.bytes(logo.bytes)
            it.ascii("\nendstream")
        }
        val xref = out.size
        out.ascii("xref\n0 ${offsets.size + 1}\n0000000000 65535 f \n")
        offsets.forEach { out.ascii(it.toString().padStart(10, '0') + " 00000 n \n") }
        out.ascii("trailer\n<< /Size ${offsets.size + 1} /Root 1 0 R /Info 5 0 R >>\nstartxref\n$xref\n%%EOF\n")
        return out.toByteArray()
    }
}
